import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Button, Container, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useExpanded, useGlobalFilter, usePagination, useTable } from "react-table";

import { getAllCollaborators, setListOptionCollaborator } from "../../../redux/slices/collaboratorSlice";

import "./CollaboratorList.css";
import duckNotFoundImg from "../../../assets/images/duckNotFound.svg";
import IconButton from "../../IconButton/IconButton";
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from "../../Icon/icons";
import AcronymIcon from "../../UI/AcronymIcon/AcronymIcon";
import ContainerSpinner from "../../UI/ContainerSpinner/ContainerSpinner";

import CollaboratorReportCSV from "../../../reports/CollaboratorReportCSV";

const DEFAULT_LIST_OPTION = "DandD";
const TABLE_ROW_SKILL_ITEMS = 5;
const TABLE_COLUMNS = [
  { accessor: "acronym_icon" },
  {
    Header: "Colaborador",
    accessor: "user_name",
  },
  {
    Header: "Time",
    accessor: "team_name",
  },
  {
    Header: "Senioridade",
    accessor: "seniority_name",
  },
  {
    Header: "Projeto",
    accessor: "project_name",
  },
  {
    Header: "Habilidades",
    accessor: "skills",
  },
  { accessor: "chevron_icon" },
];

function CollaboratorList({ setSubheaderUpdatedAt }) {
  const dispatch = useDispatch();
  const collaboratorSlice = useSelector((state) => state.collaboratorSlice);

  const [filteredCollaborators, setFilteredCollaborators] = useState([]);
  const [filters, setFilters] = useState([]);
  const [filter, setFilter] = useState("");

  const { prepareRow, getTableProps, getTableBodyProps, headerGroups, page } = useTable(
    {
      columns: TABLE_COLUMNS,
      data: filteredCollaborators,
      initialState: { pageIndex: 0, pageSize: Number.MAX_SAFE_INTEGER },
    },
    useGlobalFilter,
    useExpanded,
    usePagination
  );

  const navigate = useNavigate();

  const goToCollaborator = (collaboratorId) => {
    navigate(`/collaborators/${collaboratorId}`);
  };

  const goToCollaboratorRegister = () => {
    navigate("/collaborator/register");
  };

  const onChangeViewOption = (event) => {
    dispatch(setListOptionCollaborator(event.target.value));
  };

  const onChangeFilter = (event) => {
    setFilter(event.target.value);
  };

  const onKeyDownFilter = (event) => {
    if (event.key === "Enter") {
      addFilterOption(event.target.value);
    }
  };

  const getFilterSuggestions = () => {
    let suggestions = [];

    collaboratorSlice.collaboratorList.forEach((collaborator) => {
      if (collaborator.user && !suggestions.find((suggestion) => suggestion.name === collaborator.user.name)) {
        suggestions.push({ name: collaborator.user.name, column: "user_name" });
      }

      if (collaborator.team && !suggestions.find((suggestion) => suggestion.name === collaborator.team.name)) {
        suggestions.push({
          name: collaborator.team.name,
          column: "team_name",
        });
      }

      if (
        collaborator.seniority &&
        !suggestions.find((suggestion) => suggestion.name === collaborator.seniority.name)
      ) {
        suggestions.push({
          name: collaborator.seniority.name,
          column: "seniority_name",
        });
      }

      if (collaborator.project && !suggestions.find((suggestion) => suggestion.name === collaborator.project.name)) {
        suggestions.push({
          name: collaborator.project.name,
          column: "project_name",
        });
      }

      collaborator.skills?.forEach((skill) => {
        if (!suggestions.find((suggestion) => suggestion.name === skill)) {
          suggestions.push({ name: skill, column: "skill" });
        }
      });
    });

    return suggestions;
  };

  const addFilterOption = (option) => {
    if (option === "" || filters.some((filter) => filter === option)) {
      return;
    }

    setFilters((filters) => [...filters, option]);
    setFilter("");
  };

  const removeFilterOption = (option) => {
    const newFilters = filters.filter((filter) => filter !== option);
    setFilters(newFilters);
  };

  const applyFilter = useCallback(() => {
    let filteredCollaborators = collaboratorSlice.collaboratorList;

    if (filters.length === 0) {
      setFilteredCollaborators(filteredCollaborators);
    } else {
      filters.forEach((filter) => {
        filter = filter.toLowerCase();

        filteredCollaborators = filteredCollaborators.filter(
          (row) =>
            row.user.name?.toLowerCase().includes(filter.toLowerCase()) ||
            row.team.name?.toLowerCase().includes(filter.toLowerCase()) ||
            row.seniority.name?.toLowerCase().includes(filter.toLowerCase()) ||
            row.project?.name?.toLowerCase().includes(filter.toLowerCase()) ||
            row.skills.filter((skill) => skill.toLowerCase().includes(filter.toLowerCase())).length > 0
        );
      });

      setFilteredCollaborators(filteredCollaborators);
    }
  }, [collaboratorSlice.collaboratorList, filters]);

  const resetFilter = () => {
    setFilters([]);
    setFilteredCollaborators(collaboratorSlice.collaboratorList);
  };

  const getFilterView = () => {
    return (
      <Container className="mb-2 px-0">
        <Row>
          <Col style={{ flexGrow: 0 }}>
            <input
              list="filterSuggestionOptions"
              className="filter-input"
              placeholder="Pesquisar"
              value={filter}
              onChange={onChangeFilter}
              onKeyDown={onKeyDownFilter}
            />
            <datalist id="filterSuggestionOptions">
              {getFilterSuggestions().map((item, key) => (
                <option key={key} value={item.name} />
              ))}
            </datalist>
          </Col>
          <Col>
            {filters.length > 0 && (
              <div className="filter-options-container">
                <span className="filter-options-text">Pesquisando por:</span>
                {filters.map((filter, index) => (
                  <div className="filter-option" key={index}>
                    {filter}
                    <IconButton style={{ paddingRight: 0 }} onClick={() => removeFilterOption(filter)}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Col>
          <Col style={{ display: "flex", flexGrow: 0 }}>
            <CollaboratorReportCSV buttonClassName="export-report-button" buttonVariant="outline-primary" />
            <Button className="register-collaborator-button" onClick={goToCollaboratorRegister}>
              Cadastrar Colaborador
            </Button>
          </Col>
        </Row>
      </Container>
    );
  };

  const getTableView = () => {
    const isFilteredNotFound = filteredCollaborators.length === 0 && filters.length > 0;
    const tableClassName = "collaborator" + (isFilteredNotFound ? "" : " table-hover");

    return (
      <div className="table-container collaborator">
        <table {...getTableProps()} className={tableClassName}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>{isFilteredNotFound ? getNotFoundView() : getTableRowsView()}</tbody>
        </table>
      </div>
    );
  };

  const getTableRowsView = () => {
    return page.map((row) => {
      prepareRow(row);
      return (
        <tr {...row.getToggleRowExpandedProps()} key={row.original.id} title="Expandir/Contrair Item">
          <td className="acronym-icon-col">
            <AcronymIcon
              name={row.original.user.name}
              width="28px"
              height="28px"
              fontSize="12px"
              borderColor="#000000"
              onClick={() => goToCollaborator(row.original.id)}
            />
          </td>
          <td className="user-name-col">
            <p onClick={() => goToCollaborator(row.original.id)}>{row.original.user.name}</p>
          </td>
          <td className="team-name-col">
            <p>{row.original.team.name}</p>
          </td>
          <td className="seniority-name-col">
            <p>{row.original.seniority.name}</p>
          </td>
          <td className="project-name-col">
            <p>{row.original.project?.name}</p>
          </td>
          <td className="skills-col">
            {row.original.skills?.map((item, index) => {
              if (row.isExpanded || index < TABLE_ROW_SKILL_ITEMS) {
                return <p key={index}>{item}</p>;
              }
              if (index === TABLE_ROW_SKILL_ITEMS + 1) {
                return <p key="skills-col-dots">...</p>;
              }
              return null;
            })}
          </td>
          <td className="chevron-icon-col">
            <p>{row.isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</p>
          </td>
        </tr>
      );
    });
  };

  const getNotFoundView = () => {
    return (
      <tr>
        <td colSpan={6} className="filter-not-found">
          <img src={duckNotFoundImg} alt="Ilustração" />
          <p>
            Ops! Não encontramos ninguém com essa busca. Quer <br />
            tentar denovo?&nbsp;
            <span className="filter-reset" onClick={resetFilter}>
              Clique aqui para limpar todos os filtros
            </span>
          </p>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    applyFilter();

    if (collaboratorSlice.collaboratorList.length) {
      let updatedAt = Math.max(...collaboratorSlice.collaboratorList.map((c) => new Date(c.updated_at)));
      setSubheaderUpdatedAt(updatedAt);
    } else {
      setSubheaderUpdatedAt(undefined);
    }
  }, [applyFilter, collaboratorSlice.collaboratorList, filters, setSubheaderUpdatedAt]);

  useEffect(() => {
    dispatch(getAllCollaborators({ filter: collaboratorSlice.collaboratorListOption }));
  }, [dispatch, collaboratorSlice.collaboratorListOption]);

  return (
    <Container>
      <Row>
        <Col className="screen-header">
          <p className="screen-header-title">Lista de Colaboradores</p>
        </Col>

        <Col className="list-option-container">
          <span>Exibir:</span>
          <Form.Select defaultValue={collaboratorSlice.collaboratorListOption} onChange={onChangeViewOption}>
            <option key={0} value={DEFAULT_LIST_OPTION}>
              D&amp;D
            </option>
            <option key={1} value="OPERATIONS">
              Operações
            </option>
            <option key={2} value="ARCHIVED">
              Arquivados
            </option>
          </Form.Select>
        </Col>
      </Row>

      <Row>
        <Col className="align-items-center justify-content-center py-2">
          {getFilterView()}

          {collaboratorSlice.loading ? <ContainerSpinner /> : <>{getTableView()}</>}
        </Col>
      </Row>
    </Container>
  );
}

export { DEFAULT_LIST_OPTION };
export default CollaboratorList;
